import OverviewChartIcon from '@assets/jsx-icon/OverviewChartIcon';
import { pushError, pushSuccess } from '@components/toast';
import { sp111ConfigureRequestHttpController, geoZoneController } from '@controllers/index';
import { CameraType } from '@EcamModel/controllers/ICameraHttpController';
import { Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import color from '@theme/Colors';
import Konva from 'konva';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Circle, Image, Layer, Line, Stage, Text } from 'react-konva';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCameraDetailContext } from '../../../../hook/CameraDetailsProvider';
import { typeTextMap } from '../../../../hook/GeoZonesNavigateHandler';
import { base64ToHTMLImageElement } from '../ptz/components/PopupConfigViewCameraPTZ';
import GeoZonesHistoriesCameraPTZ from '../ptz/photoHistories/components/GeoZonesHistoriesCameraPTZ';
import { BaseBoxDecoration, PreviewImageWithGeoZone } from '../solar/components/HistoriesPhoto';
import BaseTableGeoZonesDetail from '../components/BaseTableGeoZonesDetail';
import { GeoZones } from '@EcamModel/model';
import usePopUp from '@hooks/usePopUp';
import PopUpWarning from '@components/PopUpWarning';
import { getCenterFrame } from '@pages/cameras/details/utils';
import GeoZonesHistoriesCameraSolar from '../solar/components/GeoZonesHistoriesCameraSolar';
import { useQueryClient } from 'react-query';

export default function GeoZonesOverViewSP111() {
    const { locationId, geoZoneId } = useParams();
    const location = useLocation();
    const state = location?.state;
    const queryClient = useQueryClient();

    const navigate = useNavigate();

    const {
        camera,
        containerRef,
        containerSize,
        timeZone,
        captureAtLatest,
        prevGeoZonesCameraSP111,
        scaledPrevGeoZoneCameraSP111,
        scaleNotFullCameraSP111,
        scaleFullCameraSP111,
        imageElementSP111,
        setCamera,
        setImageElementSP111,
    } = useCameraDetailContext();
    const popUpWarningDeleteGeoZones = usePopUp();

    const stageRef = useRef<Konva.Stage>(null);
    const [openIMGPreview, setOpenIMGPreview] = useState<boolean>(false);
    const [blobBase64, setBlobBase64] = useState<string>('');
    const [selectedGeoZone, setSelectedGeoZone] = useState<GeoZones>({} as GeoZones);
    const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);

    const getPhotoSnapShotSP111 = () => {
        sp111ConfigureRequestHttpController
            .getPhotoSnapShot({
                ip: camera.IP,
                authenticate: {
                    password: camera.PassWord,
                    username: camera.UserName,
                },
            })
            .then((res) => {
                setBlobBase64(res);
            })
            .catch((err) => {
                pushError(err.response?.data?.message);
            });
    };

    const handleClickImagePreview = () => {
        setOpenIMGPreview(true);
    };

    useEffect(() => {
        const convertBase64ToImage = async () => {
            try {
                const imgElement = await base64ToHTMLImageElement(blobBase64);
                setImageElementSP111(imgElement);
            } catch (error) {
                console.error('Failed to convert base64 to image', error);
            }
        };
        convertBase64ToImage();
    }, [blobBase64]);

    useEffect(() => {
        if (!Object.values(camera).length) return;
        getPhotoSnapShotSP111();
    }, [camera]);

    return (
        <Stack>
            <Box padding={'0px 16px 16px 16px'} borderRadius={2} width={'100%'}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
                    <Stack alignItems={'center'} spacing={1} direction={'row'}>
                        <OverviewChartIcon width={32} height={32} viewBox="0 0 32 32" />
                        <Typography variant="h5" color={color.darkPrimary}>
                            Overview
                        </Typography>
                    </Stack>
                </Stack>

                <Divider orientation="horizontal" flexItem sx={{ borderColor: color.grey300, marginBottom: '16px' }} />

                {!imageElementSP111 ? (
                    <Stack
                        direction="row"
                        justifyContent="center"
                        alignItems="center"
                        minHeight={500}
                        bgcolor={color.white}
                    >
                        <CircularProgress />
                    </Stack>
                ) : (
                    <div
                        ref={containerRef}
                        style={{
                            width: '65%',
                            margin: '0 auto',
                            height: containerSize.height,
                            position: 'relative',
                            borderRadius: 10,
                        }}
                    >
                        <Stage
                            ref={stageRef}
                            width={containerSize.width}
                            height={containerSize.height}
                            onClick={handleClickImagePreview}
                            style={{
                                cursor: 'pointer',
                            }}
                        >
                            <Layer>
                                <Image
                                    image={imageElementSP111!}
                                    scaleX={scaleNotFullCameraSP111}
                                    scaleY={scaleNotFullCameraSP111}
                                />
                                {!!scaledPrevGeoZoneCameraSP111.length &&
                                    scaledPrevGeoZoneCameraSP111.map((frame, frameIndex) => {
                                        return <GeoZonesHistoriesCameraSolar frame={frame} index={frameIndex} />;
                                    })}
                            </Layer>
                        </Stage>

                        <PreviewImageWithGeoZone
                            open={openIMGPreview}
                            onClose={() => {
                                setOpenIMGPreview(false);
                            }}
                            imgSrc={imageElementSP111!}
                            scaleX={scaleFullCameraSP111}
                            scaleY={scaleFullCameraSP111}
                            cameraType={CameraType.Solar}
                            geoZones={prevGeoZonesCameraSP111}
                        />

                        <BaseBoxDecoration timeZone={timeZone} date={captureAtLatest ?? moment().toDate()} />
                    </div>
                )}

                <PopUpWarning
                    {...popUpWarningDeleteGeoZones}
                    title="Confirm"
                    message="Are you sure you want to delete this geo-zone?"
                    isLoading={isLoadingSave}
                    onConfirm={() => {
                        setIsLoadingSave(true);
                        geoZoneController
                            .delete(selectedGeoZone.Id?.toString()!)
                            .then((res) => {
                                queryClient.invalidateQueries(['trailAuditEvent']);
                                setCamera((prev) => ({
                                    ...prev,
                                    GeoZones: prev.GeoZones?.filter((item) => item.Id !== selectedGeoZone.Id),
                                }));
                                pushSuccess('Deleted geo-zone successfully');
                                popUpWarningDeleteGeoZones.onClose();
                            })
                            .catch((err) => {
                                pushError(err.response?.data?.message);
                            })
                            .finally(() => {
                                setIsLoadingSave(false);
                            });
                    }}
                />
            </Box>

            <Stack padding={2} borderRadius={2} bgcolor={color.white} width={'100%'} mt={2}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
                    <Typography variant="h5" color={color.darkPrimary}>
                        Geo-zones ({camera.GeoZones?.length})
                    </Typography>
                    {!camera.Deleted && (
                        <Button
                            variant="contained"
                            disabled={!imageElementSP111}
                            onClick={() => {
                                navigate(`add/${typeTextMap[CameraType.SP111]}`);
                            }}
                        >
                            Add geo-zones
                        </Button>
                    )}
                </Stack>

                <Divider orientation="horizontal" flexItem sx={{ borderColor: color.grey300 }} />

                <BaseTableGeoZonesDetail
                    rows={camera.GeoZones || []}
                    cameraDeleted={!camera.Deleted}
                    handleDelete={(item) => {
                        setSelectedGeoZone(item);
                        popUpWarningDeleteGeoZones.setTrue();
                    }}
                    handleEdit={(item) => {
                        setSelectedGeoZone(item);
                        navigate(`edit/${item.Id}/${typeTextMap[CameraType.SP111]}`, {
                            state: {
                                selectedGeoZone: item,
                            },
                        });
                    }}
                />
            </Stack>
        </Stack>
    );
}
